/**
 * About Page - WoodAlps Company Story and History
 * 
 * This comprehensive about page showcases the WoodAlps story with:
 * - Company history with timeline and milestone images
 * - Portable text content rendering from Sanity CMS
 * - Fallback content system for offline/missing data scenarios
 * - Social media integration for brand engagement
 * - Product lineup showcase within company context
 * - Responsive design with optimized images
 * 
 * Content Structure:
 * - Hero section with company introduction
 * - Historical timeline with milestone images
 * - Mission and values presentation
 * - Social media and contact integration
 * - Product showcase for brand consistency
 * 
 * Features:
 * - Server-side rendering with ISR support
 * - Graceful content fallbacks for reliability
 * - SEO optimization with meta tags
 * - Accessible design and navigation
 * 
 * @description Company about page with history, mission, and brand story
 */

import Head from 'next/head';
import Image from 'next/image';
import { PortableText } from '@portabletext/react';
import { getOurHistoryData, getHomePageData } from '../src/app/studio/lib/sanity';
import { urlForImage } from '../src/app/studio/lib/image';
import { sanityLoader } from '../utils/sanityLoader';
import Layout from '../components/layout/Layout';
import ProductLineupSection from '../components/sections/ProductLineupSection';
import SocialMediaContact from '../components/sections/SocialMediaContact';
import { useTranslation } from '../src/hooks/useTranslation';



// Fallback images for reliable Our History section display
const FALLBACK_IMAGES = {
  rowingImage: '/images/history/rowing.jpg',
  frameCrack: '/images/history/frame-crack.jpg',
  firstWorkshop: '/images/history/workshop.jpg',
  cnc: '/images/history/workshop-cnc.jpg',
  firstPrototype: '/images/history/first-prototype.jpg',
  brandExhibition: '/images/history/brand-exhibition.jpg',
  montDeGrange: '/images/history/mont-de-grange.jpg'
};


const renderPortableText = (portableText, fallbackText) => {
  // Check if portableText exists, is an array, has content, and that content has text
  if (portableText && 
      Array.isArray(portableText) && 
      portableText.length > 0 && 
      portableText.some(block => 
        block.children && 
        block.children.length > 0 && 
        block.children.some(child => child.text && child.text.trim().length > 0)
      )
  ) {
    return <PortableText value={portableText} components={portableTextComponents} />;
  }
  // Return fallback text if any of the above conditions aren't met
  return <p>{fallbackText}</p>;
};

// Custom components for the PortableText renderer
const portableTextComponents = {
  marks: {
    strong: ({children}) => <strong className="font-bold">{children}</strong>,
    em: ({children}) => <em>{children}</em>,
    neuropol: ({children}) => <span className="font-neuropol">{children}</span>,
    montserrat: ({children}) => <span className="font-montserrat">{children}</span>,
    underline: ({children}) => <span className="underline">{children}</span>,
  },
  block: {
    normal: ({children}) => <p className="mb-10 text-gray-700 text-base sm:text-lg leading-relaxed">{children}</p>,
    h1: ({children}) => <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-10">{children}</h1>,
    h2: ({children}) => <h2 className="text-2xl sm:text-3xl font-bold mb-8">{children}</h2>,
    h3: ({children}) => <h3 className="text-xl sm:text-2xl font-bold mb-6">{children}</h3>,
    h4: ({children}) => <h4 className="text-lg sm:text-xl font-bold mb-6">{children}</h4>,
    blockquote: ({children}) => <blockquote className="border-l-4 border-accent-color pl-4 italic my-8">{children}</blockquote>,
  },
};

// Image Section Component
const ImageSection = ({ images, layout, customMaxWidth, customMaxHeight }) => {
  if (!images || images.length === 0) return null;

  // Determine if we should use a multi-column layout
  const useMultiColumn = layout === 'double' || images.length > 1;
  
  // Base container classes - different max widths for single vs double
  const containerClasses = `my-16 sm:my-20 md:my-24 ${customMaxWidth || (useMultiColumn ? 'max-w-5xl' : 'max-w-4xl')} mx-auto`;
  
  // Default max heights - taller for single images, shorter for doubles
  const defaultMaxHeight = customMaxHeight || (useMultiColumn ? 'max-h-[28rem]' : 'max-h-[36rem]');

  // Calculate consistent aspect ratio for all images in this section
  // If we have fallback images with defined aspect ratios, use the first one
  // If we have mixed content, prioritize fallback aspect ratio for consistency
  const firstFallbackAspectRatio = images.find(img => img.aspectRatio)?.aspectRatio;
  const consistentAspectRatio = firstFallbackAspectRatio || 
    (useMultiColumn ? 4/3 : 16/9); // Default to 4/3 for multi-column, 16/9 for single

  return (
    <div className={containerClasses}>
      <div className={`${useMultiColumn ? 'grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-10' : ''}`}>
        {images.map((item, index) => {
          // Use consistent aspect ratio for all images in this section
          const aspectRatio = consistentAspectRatio;
          
          return (
            <figure key={index} className="mb-8 md:mb-0">
              {item.asset?.metadata?.dimensions ? (
                // For Sanity images - use same approach as fallback images for consistent sizing
                <div className={`w-full ${defaultMaxHeight} relative overflow-hidden bg-gray-100`}
                     style={{ aspectRatio: aspectRatio, height: 'auto' }}>
                  <Image
                    src={item.asset.url || '/images/placeholder.jpg'}
                    alt={item.alt || item.caption || 'WoodAlps history image'}
                    fill
                    loader={sanityLoader}
                    sizes="(max-width: 640px) 100vw, (max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    className="object-cover"
                    style={{ objectPosition: item.position || 'center' }}
                  />
                </div>
              ) : (
                // For fallback images - use the aspectRatio property if available
                <div className={`w-full ${defaultMaxHeight} relative overflow-hidden bg-gray-100`}
                     style={{ aspectRatio: aspectRatio, height: 'auto' }}>
                  {item.fallbackSrc ? (
                    <Image
                      src={item.fallbackSrc}
                      alt={item.alt || item.caption || 'WoodAlps history image'}
                      fill
                      sizes="(max-width: 640px) 100vw, (max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      className="object-cover"
                      style={{ objectPosition: item.position || 'center' }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-200">
                      <p className="text-gray-500">No image available</p>
                    </div>
                  )}
                </div>
              )}
              {item.caption && (
                <figcaption className="text-sm md:text-base text-gray-600 mt-3 text-center">
                  {item.caption}
                </figcaption>
              )}
            </figure>
          );
        })}
      </div>
    </div>
  );
};

// Default fallback image for sections
const getDefaultFallbackImage = (sectionName) => {
  switch(sectionName) {
    case 'rowingImages':
      return [{ 
        fallbackSrc: '/images/history/rowing.jpg', 
        caption: 'Competing in rowing at a high level',
        aspectRatio: 16/4 
      }];
    case 'frameCrackImages':
      return [
        { 
          fallbackSrc: '/images/history/frame-crack.jpg', 
          caption: 'Mountain bike frame crack',
          aspectRatio: 1/1 
        },
        { 
          fallbackSrc: '/images/history/frame-crack2.jpg', 
          caption: 'Road bike frame crack',
          aspectRatio: 1/1 
        }
      ];
    case 'workshopImages':
      return [
        { 
          fallbackSrc: '/images/history/workshop.jpg', 
          caption: 'First workshop - exterior view',
          aspectRatio: 4/3
        },
        { 
          fallbackSrc: '/images/history/workshop-cnc.jpg', 
          caption: 'First workshop - interior view',
          aspectRatio: 4/3
        }
      ];
    case 'prototypeImages':
      return [{ 
        fallbackSrc: '/images/history/first-prototype.jpg', 
        caption: 'The very first prototype',
        aspectRatio: 16/15
      }];
    case 'exhibitionImages':
      return [
        { 
          fallbackSrc: '/images/history/brand-exhibition.jpg', 
          caption: 'First brand exhibition - stand view',
          aspectRatio: 3/2
        },
        { 
          fallbackSrc: '/images/history/brand-exhibition2.jpg', 
          caption: 'First brand exhibition - bikes on display',
          aspectRatio: 3/2
        }
      ];
    case 'currentWorkshopImages':
      return [
        { 
          fallbackSrc: '/images/history/currentWorkShop.jpg', 
          caption: 'Current workshop - overview',
          aspectRatio: 16/9
        },
        { 
          fallbackSrc: '/images/history/currentWorkshop-cnc.jpg', 
          caption: 'Current workshop - CNC machines',
          aspectRatio: 16/9
        }
      ];
    default:
      return [{ 
        fallbackSrc: '/images/placeholder.jpg', 
        caption: 'Image placeholder',
        aspectRatio: 16/9
      }];
  }
};

export default function About({ ourHistoryData, homePageData }) {
  const { t } = useTranslation();
  // Helper function to check if a section contains content
  const hasContent = (section) => {
    if (!section) return false;
    if (Array.isArray(section)) return section.length > 0;
    
    // For PortableText sections, check if they have content
    if (Array.isArray(section) && section.length > 0) {
      return section.some(block => 
        block.children && 
        block.children.length > 0 && 
        block.children.some(child => child.text && child.text.trim().length > 0)
      );
    }
    
    return true;
  };

  // Helper function to render a section of text
  const renderTextSection = (content) => {
    if (!hasContent(content)) return null;
    return (
      <div className="text-left">
        <PortableText value={content} components={portableTextComponents} />
      </div>
    );
  };

  // Helper function to render an image section
  const renderImageSection = (images, layout, customMaxWidth, customMaxHeight) => {
    if (!images || images.length === 0) return null;
    
    // If no layout is specified, default to 'double' for multiple images, 'single' for one image
    const sectionLayout = layout || (images.length > 1 ? 'double' : 'single');
    
    return <ImageSection images={images} layout={sectionLayout} customMaxWidth={customMaxWidth} customMaxHeight={customMaxHeight} />;
  };

  // Social section data
  const socialSection = {
  title: t('socialSection.title'),
  fallbackDescription: t('socialSection.description'),
  facebookLink: 'https://facebook.com/woodalps',
  instagramLink: 'https://www.instagram.com/woodalps_wooden_bikes/'
  };

  // Contact info data
  const contactInfo = {
  title: t('contactSection.title'),
  fallbackDescription: t('contactSection.description'),
  buttonText: t('contactSection.buttonText'),
  buttonLink:  '/contact'
  };


  return (
    <>
      <Head>
        <title>About Us | WoodAlps</title>
        <meta
          name="description"
          content="Learn about WoodAlps, our mission, story, and the passionate team behind our handcrafted wooden bicycles."
        />
      </Head>

      {/* Hero Banner */}
      <div className="relative h-[50vh] sm:h-[60vh] md:h-[70vh] bg-deep-black w-full">
        <div className="absolute inset-0 bg-gradient-to-b from-deep-black/70 via-deep-black/50 to-transparent z-10"></div>
        <div className="relative h-full w-full">
          <Image 
            src={ourHistoryData?.heroImage ? urlForImage(ourHistoryData.heroImage) : "/images/ourHistoryhero.jpeg"}
            alt={ourHistoryData?.heroImage?.alt || "Mountain view near workshop"}
            fill
            priority
            sizes="100vw"
            className="object-cover"
          />
        </div>
        <div className="absolute inset-0 flex flex-col justify-center items-center text-center px-4 z-20">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            {t('about.titleHero')}
          </h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-2xl">
            {t('about.subtitleHero')}
          </p>
        </div>
      </div>

      {/* Our History Section */}
      <section className="section py-18 sm:py-12 md:py-14 pb-2 sm:pb-4 md:pb-6 bg-white">
        <div className="container-custom">
          <div className="mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6">{ourHistoryData?.title || 'Our History'}</h2>
            <div className="w-20 h-1 mx-auto mb-16"></div>
            
            <div className="space-y-10 sm:space-y-12 md:space-y-16">
              {/* Introduction */}
              <div className="max-w-3xl mx-auto">
                {renderTextSection(ourHistoryData?.intro)}
            </div>
              
              {/* Early Years */}
              <div className="max-w-3xl mx-auto">
                {renderTextSection(ourHistoryData?.earlyYearsParagraph)}
              </div>
              {renderImageSection(ourHistoryData?.rowingImages || getDefaultFallbackImage('rowingImages'), 'single')}
              
              {/* Frame Cracks */}
              <div className="max-w-3xl mx-auto">
                {renderTextSection(ourHistoryData?.frameCracksParagraph)}
            </div>
              {renderImageSection(ourHistoryData?.frameCrackImages || getDefaultFallbackImage('frameCrackImages'), 'double')}
              
              {/* Career */}
              <div className="max-w-3xl mx-auto">
                {renderTextSection(ourHistoryData?.careerParagraph)}
          </div>
              
              {/* CNC Machine */}
              <div className="max-w-3xl mx-auto">
                {renderTextSection(ourHistoryData?.cncParagraph)}
        </div>
              
              {/* Wood Materials */}
              <div className="max-w-3xl mx-auto">
                {renderTextSection(ourHistoryData?.woodMaterialsParagraph)}
          </div>

              {/* First Workshop */}
              <div className="max-w-3xl mx-auto">
                {renderTextSection(ourHistoryData?.firstWorkshopParagraph)}
              </div>
              {renderImageSection(ourHistoryData?.workshopImages || getDefaultFallbackImage('workshopImages'), 'double')}
              
              {/* Prototypes */}
              <div className="max-w-xl mx-auto">
                {renderTextSection(ourHistoryData?.prototypesParagraph)}
              </div>
              {renderImageSection(ourHistoryData?.prototypeImages || getDefaultFallbackImage('prototypeImages'), 'single', 'max-w-2xl', 'max-h-[24rem]')}
              
              {/* Brand Creation */}
              <div className="max-w-3xl mx-auto">
                {renderTextSection(ourHistoryData?.brandCreationParagraph)}
              </div>
              {renderImageSection(ourHistoryData?.exhibitionImages || getDefaultFallbackImage('exhibitionImages'), 'double')}
              
              {/* Mission */}
              <div className="max-w-3xl mx-auto">
                {renderTextSection(ourHistoryData?.missionParagraph)}
        </div>
              
              {/* Current Workshop */}
              <div className="max-w-3xl mx-auto">
                {renderTextSection(ourHistoryData?.currentWorkshopParagraph)}
              </div>
              {renderImageSection(ourHistoryData?.currentWorkshopImages || getDefaultFallbackImage('currentWorkshopImages'), 'double')}
              
              {/* Conclusion */}
              <div className="max-w-3xl mx-auto">
                {renderTextSection(ourHistoryData?.conclusionParagraph)}
                <div className="flex justify-center mt-8 mb-4">
                  <Image 
                    src="/images/name.jpg" 
                    alt="Signature" 
                    width={200} 
                    height={100} 
                    className="object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Lineup Section */}
      <ProductLineupSection 
        data={{ 
          title: t('about.productLineupSection'),
          grooty: homePageData?.productLineupSection?.grooty,
          branchy: homePageData?.productLineupSection?.branchy,
          yggy: homePageData?.productLineupSection?.yggy,
          products: homePageData?.productLineupSection?.products
        }}
        smallTitle={true}
        showFlowchart={false}
        reducedTopMargin={true}
        bgColor="bg-white"
      />

      {/* Social Media and Contact */}
      <section className="section bg-white pt-8 pb-16">
        <div className="container-custom">
          <SocialMediaContact 
            containerClassName="bg-white p-10 mx-auto justify-center items-center rounded-xl"
            socialSection={socialSection}
            contactInfo={contactInfo}
            renderPortableText={renderPortableText}
          />
        </div>
      </section>
    </>
  );
} 

// Get static props to fetch our history data at build time
export async function getStaticProps({ preview = false, locale = 'en' }) {
  const ourHistoryData = await getOurHistoryData(preview, locale);
  const homePageData = await getHomePageData(preview, locale);
  
  return {
    props: {
      ourHistoryData,
      homePageData
    },
    revalidate: process.env.VERCEL_ENV === 'preview' ? 1 : 86400, // 24 hours
  };
}

// Use Layout component as the page layout
About.getLayout = function getLayout(page) {
  return <Layout transparentHeader={true} headerShadow={true}>{page}</Layout>;
}; 